import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Configuration for the side panel
 */
interface SidePaneConfig {
  visible: boolean;
  title: string;
  component: Type<any> | null;
  inputs?: Record<string, any>;
}

/**
 * Service to manage the layout's right panel state
 * Provides methods to open, close, and update the right panel dynamically
 */
@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private panelConfig$ = new BehaviorSubject<SidePaneConfig>({
    visible: false,
    title: '',
    component: null,
    inputs: {}
  });

  /**
   * Get the current panel configuration as an Observable
   */
  get panelConfig(): Observable<SidePaneConfig> {
    return this.panelConfig$.asObservable();
  }

  /**
   * Get the current panel visibility as an Observable
   */
  get isVisible$(): Observable<boolean> {
    return new Observable(observer => {
      this.panelConfig$.subscribe(config => {
        observer.next(config.visible);
      });
    });
  }

  /**
   * Get the current panel configuration value
   */
  get currentConfig(): SidePaneConfig {
    return this.panelConfig$.value;
  }

  /**
   * Open the side panel with specified component and inputs
   * @param title - Panel title to display in header
   * @param component - Angular component to render in panel
   * @param inputs - Input data to pass to the component
   */
  openPanel(title: string, component: Type<any>, inputs?: Record<string, any>): void {
    this.panelConfig$.next({
      visible: true,
      title,
      component,
      inputs: inputs || {}
    });
  }

  /**
   * Close the side panel and clear its content
   */
  closePanel(): void {
    this.panelConfig$.next({
      visible: false,
      title: '',
      component: null,
      inputs: {}
    });
  }

  /**
   * Toggle the panel visibility
   */
  togglePanel(): void {
    this.panelConfig$.next({
      ...this.panelConfig$.value,
      visible: !this.panelConfig$.value.visible
    });
  }

  /**
   * Update the component inputs without changing other panel state
   * @param inputs - New input data to merge with existing inputs
   */
  updatePanelInputs(inputs: Record<string, any>): void {
    this.panelConfig$.next({
      ...this.panelConfig$.value,
      inputs: { ...this.panelConfig$.value.inputs, ...inputs }
    });
  }
}
